import React, { useEffect, useRef, useState } from "react";
import { Bell, Check, X, ShieldAlert } from "lucide-react";
import { usePermissionRequests } from "@/context/PermissionRequestContext";
import { Link } from "react-router-dom";

/**
 * Header bell listing admin permission requests awaiting this user's decision.
 *
 * Approving here is the only way an admin gains access, so the copy states the
 * scope plainly before the user commits rather than hiding it behind a generic
 * "allow" button.
 */
export default function PermissionRequestBell() {
  const { pending, pendingCount, loading, respond } = usePermissionRequests();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await respond(id, decision);
    } catch {
      /* the provider already surfaced the error */
    } finally {
      setBusyId(null);
    }
  };

  if (loading || pendingCount === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded hover:bg-gray-100"
        data-testid="permission-bell"
        aria-label={`${pendingCount} permission request${pendingCount > 1 ? "s" : ""}`}
        title="Permission requests"
      >
        <Bell className="w-4 h-4" />
        <span
          className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[var(--primary)] text-white text-[10px] font-semibold grid place-items-center"
          data-testid="permission-bell-count"
        >
          {pendingCount}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-80 bg-white border border-[var(--border)] rounded-lg shadow-lg overflow-hidden"
          data-testid="permission-bell-menu"
        >
          <div className="px-3 py-2 border-b border-[var(--border)] flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[var(--primary)]" />
            <span className="overline">Administrator requests</span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {pending.map((r) => (
              <div
                key={r.id}
                className="p-3 border-b border-[var(--border)] last:border-0"
                data-testid={`permission-request-${r.id}`}
              >
                <div className="text-sm">
                  <strong>{r.requested_by_email || "An administrator"}</strong> is asking
                  permission to{" "}
                  <strong>{r.scope === "modify" ? "view and edit" : "view"}</strong> your
                  data.
                </div>
                {r.reason && (
                  <div className="text-xs text-gray-600 mt-1.5 p-2 rounded bg-gray-50 border border-[var(--border)]">
                    “{r.reason}”
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mt-1.5">
                  You can change or revoke this at any time in Settings.
                </div>
                <div className="flex gap-2 mt-2.5">
                  <button
                    type="button"
                    disabled={busyId === r.id}
                    onClick={() => decide(r.id, "approve")}
                    className="btn-primary text-xs h-8 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`permission-approve-${r.id}`}
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    type="button"
                    disabled={busyId === r.id}
                    onClick={() => decide(r.id, "reject")}
                    className="text-xs h-8 px-3 border border-[var(--border)] rounded-md hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`permission-reject-${r.id}`}
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="p-2 border-t border-[var(--border)] bg-gray-50/60">
            <Link
              to="/app/settings"
              className="block text-center text-xs px-3 h-8 leading-8 border border-[var(--border)] rounded-md bg-white hover:bg-gray-50"
            >
              Permission settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}